import { useCallback, useEffect, useRef, useState } from 'react';
import type { PdfJsViewer } from './pdfViewerRuntime';

type PDFViewerInstance = InstanceType<PdfJsViewer['PDFViewer']>;
type EventBusInstance = InstanceType<PdfJsViewer['EventBus']>;

/** pdf.js zoom presets the toolbar offers, plus any number ("1.25"). */
export const PAGE_FIT = 'page-fit';
export const PAGE_WIDTH = 'page-width';

/** pdf.js FindState. */
const FIND_NOT_FOUND = 1;
const FIND_PENDING = 3;

export type PdfViewerStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface PdfFindResult {
  /** 1-based index of the selected match; 0 when there is none. */
  current: number;
  total: number;
  /** The last search finished with no match. */
  notFound: boolean;
  pending: boolean;
}

const NO_FIND: PdfFindResult = { current: 0, total: 0, notFound: false, pending: false };

export interface PdfViewerController {
  status: PdfViewerStatus;
  pageNumber: number;
  pagesCount: number;
  /** The applied scale (1 = 100%). */
  scale: number;
  /** The zoom setting: a preset ('page-fit', 'page-width') or the scale as a string. */
  scaleValue: string;
  find: PdfFindResult;
  goToPage: (page: number) => void;
  firstPage: () => void;
  previousPage: () => void;
  nextPage: () => void;
  lastPage: () => void;
  setScaleValue: (value: string) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  /** Search the text layer and highlight every match. `again` moves to the next (or previous) match. */
  search: (query: string, options?: { again?: boolean; previous?: boolean }) => void;
  /** Clears the highlights. */
  clearSearch: () => void;
}

interface Options {
  /** The PDF, or null while it downloads. */
  pdf: Blob | null;
  /** The scroll container (absolutely positioned; pdf.js requires that). */
  container: HTMLDivElement | null;
  /** Zoom to open the document at. Read when the pages first lay out. */
  initialScaleValue: string;
  /** pdf.js or the PDF failed to load or parse. */
  onError: (error: unknown) => void;
}

/**
 * Drives pdf.js's own viewer component (PDFViewer from pdfjs-dist/web/pdf_viewer.mjs)
 * inside `container`: continuous pages, a selectable text layer, zoom presets and
 * find with highlighting. pdf.js is loaded on first use (see pdfViewerRuntime.ts).
 *
 * The document is destroyed (worker freed, canvases dropped) when `pdf` or
 * `container` change and on unmount.
 */
export function usePdfViewer({ pdf, container, initialScaleValue, onError }: Options): PdfViewerController {
  const [status, setStatus] = useState<PdfViewerStatus>('idle');
  const [pageNumber, setPageNumber] = useState(0);
  const [pagesCount, setPagesCount] = useState(0);
  const [scale, setScale] = useState(1);
  const [scaleValue, setScaleValueState] = useState(initialScaleValue);
  const [find, setFind] = useState<PdfFindResult>(NO_FIND);

  const viewerRef = useRef<PDFViewerInstance | null>(null);
  const busRef = useRef<EventBusInstance | null>(null);
  const initialScaleRef = useRef(initialScaleValue);
  initialScaleRef.current = initialScaleValue;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    if (!pdf || !container) {
      setStatus('idle');
      return;
    }
    let cancelled = false;
    let loadingTask: { destroy: () => Promise<void> } | null = null;
    let resizeObserver: ResizeObserver | null = null;
    const viewerEl = document.createElement('div');
    viewerEl.className = 'pdfViewer';
    container.appendChild(viewerEl);

    setStatus('loading');
    setPageNumber(0);
    setPagesCount(0);
    setFind(NO_FIND);

    (async () => {
      const { loadPdfRuntime } = await import('./pdfViewerRuntime');
      const { pdfjs, viewer } = await loadPdfRuntime();
      if (cancelled) return;

      const eventBus = new viewer.EventBus();
      const linkService = new viewer.PDFLinkService({ eventBus });
      const findController = new viewer.PDFFindController({ eventBus, linkService });
      const pdfViewer = new viewer.PDFViewer({
        container,
        viewer: viewerEl,
        eventBus,
        linkService,
        findController,
        textLayerMode: 1,
      });
      linkService.setViewer(pdfViewer);
      viewerRef.current = pdfViewer;
      busRef.current = eventBus;

      eventBus.on('pagesinit', () => {
        if (cancelled) return;
        pdfViewer.currentScaleValue = initialScaleRef.current;
        setStatus('ready');
      });
      eventBus.on('pagechanging', (e: { pageNumber: number }) => setPageNumber(e.pageNumber));
      eventBus.on('scalechanging', (e: { scale: number; presetValue?: string }) => {
        setScale(e.scale);
        setScaleValueState(e.presetValue ?? String(e.scale));
      });
      eventBus.on('updatefindmatchescount', (e: { matchesCount: { current: number; total: number } }) => {
        setFind((f) => ({ ...f, current: e.matchesCount.current, total: e.matchesCount.total }));
      });
      eventBus.on('updatefindcontrolstate', (e: { state: number; matchesCount?: { current: number; total: number } }) => {
        setFind({
          current: e.matchesCount?.current ?? 0,
          total: e.matchesCount?.total ?? 0,
          notFound: e.state === FIND_NOT_FOUND,
          pending: e.state === FIND_PENDING,
        });
      });

      // Presets are sizes relative to the box, so re-apply them when it changes
      // (full screen, window resize, the dialog finishing its entrance).
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => {
          const v = pdfViewer.currentScaleValue;
          if (pdfViewer.pagesCount > 0 && (v === PAGE_FIT || v === PAGE_WIDTH || v === 'auto')) {
            pdfViewer.currentScaleValue = v;
          }
        });
        resizeObserver.observe(container);
      }

      const data = new Uint8Array(await pdf.arrayBuffer());
      if (cancelled) return;
      const task = pdfjs.getDocument({ data });
      loadingTask = task;
      const doc = await task.promise;
      if (cancelled) return;
      pdfViewer.setDocument(doc);
      linkService.setDocument(doc, null);
      setPagesCount(doc.numPages);
      setPageNumber(1);
    })().catch((e: unknown) => {
      if (cancelled) return;
      setStatus('error');
      onErrorRef.current(e);
    });

    return () => {
      cancelled = true;
      resizeObserver?.disconnect();
      const v = viewerRef.current;
      viewerRef.current = null;
      busRef.current = null;
      try {
        // setDocument(null) resets the viewer and drops every page's canvas.
        (v as unknown as { setDocument: (d: null) => void } | null)?.setDocument(null);
      } catch { /* already torn down */ }
      void loadingTask?.destroy().catch(() => {});
      viewerEl.remove();
    };
  }, [pdf, container]);

  const goToPage = useCallback((page: number) => {
    const v = viewerRef.current;
    if (!v || v.pagesCount === 0) return;
    const n = Math.min(Math.max(1, Math.round(page)), v.pagesCount);
    v.currentPageNumber = n;
  }, []);
  const firstPage = useCallback(() => goToPage(1), [goToPage]);
  const lastPage = useCallback(() => goToPage(viewerRef.current?.pagesCount ?? 1), [goToPage]);
  const previousPage = useCallback(() => { viewerRef.current?.previousPage(); }, []);
  const nextPage = useCallback(() => { viewerRef.current?.nextPage(); }, []);

  const setScaleValue = useCallback((value: string) => {
    setScaleValueState(value);
    const v = viewerRef.current;
    if (v && v.pagesCount > 0) v.currentScaleValue = value;
    else initialScaleRef.current = value;
  }, []);
  const zoomIn = useCallback(() => { viewerRef.current?.increaseScale(); }, []);
  const zoomOut = useCallback(() => { viewerRef.current?.decreaseScale(); }, []);

  const search = useCallback((query: string, options?: { again?: boolean; previous?: boolean }) => {
    const bus = busRef.current;
    if (!bus) return;
    if (!query) {
      bus.dispatch('findbarclose', { source: null });
      setFind(NO_FIND);
      return;
    }
    bus.dispatch('find', {
      source: null,
      type: options?.again ? 'again' : '',
      query,
      caseSensitive: false,
      entireWord: false,
      highlightAll: true,
      findPrevious: !!options?.previous,
      matchDiacritics: false,
    });
  }, []);
  const clearSearch = useCallback(() => {
    busRef.current?.dispatch('findbarclose', { source: null });
    setFind(NO_FIND);
  }, []);

  return {
    status, pageNumber, pagesCount, scale, scaleValue, find,
    goToPage, firstPage, previousPage, nextPage, lastPage,
    setScaleValue, zoomIn, zoomOut, search, clearSearch,
  };
}
